import { Badge, Button, Select, Text } from "@mantine/core";
import { IconRoute } from "@tabler/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { DataTable, FilterBar, PageHeader, useSearchParamState } from "@how/ui";
import { PersonDrawer } from "../people/PersonDrawer";
import { PathwayModal } from "./PathwayModal";
import {
  STANDINGS,
  STANDING_COLOR,
  STANDING_LABEL,
  useMembershipList,
  type MembershipRow,
  type Standing,
} from "./membership-queries";

const columns: ColumnDef<MembershipRow>[] = [
  {
    accessorKey: "lastName",
    header: "Person",
    enableSorting: false,
    cell: (ctx) => (
      <Text size="sm" fw={600}>
        {ctx.row.original.firstName} {ctx.row.original.lastName}
      </Text>
    ),
  },
  {
    accessorKey: "standing",
    header: "Standing",
    enableSorting: false,
    cell: (ctx) => (
      <Badge variant="light" color={STANDING_COLOR[ctx.row.original.standing]}>
        {STANDING_LABEL[ctx.row.original.standing]}
      </Badge>
    ),
  },
  {
    accessorKey: "since",
    header: "Since",
    enableSorting: false,
    cell: (ctx) => (
      <Text size="sm">
        {new Date(`${ctx.row.original.since}T00:00:00`).toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}
      </Text>
    ),
  },
  {
    accessorKey: "stepsDone",
    header: "Pathway",
    enableSorting: false,
    cell: (ctx) => {
      const r = ctx.row.original;
      return r.pathway === null ? (
        <Text size="sm" c="dimmed">
          —
        </Text>
      ) : (
        <Text size="sm">
          {String(r.stepsDone)} of {String(r.stepsTotal)} · {r.pathway}
        </Text>
      );
    },
  },
  {
    accessorKey: "note",
    header: "Note",
    enableSorting: false,
    cell: (ctx) => (
      <Text size="sm" c="dimmed" lineClamp={1}>
        {ctx.row.original.note ?? ""}
      </Text>
    ),
  },
];

const PAGE = { pageIndex: 0, pageSize: 500 };

/**
 * Membership (task 14): everyone with a standing, newest first, filtered by standing; a row opens the record, where
 * the Pathway section changes the standing and ticks steps. The pathway's steps are edited here.
 */
export function MembershipPage() {
  const canManage = usePermission("membership.manage", { write: true });
  const [search, setSearch] = useSearchParamState("q", "");
  const [standingParam, setStandingParam] = useSearchParamState("standing", "");
  const [selected, setSelected] = useSearchParamState("person", "");
  const [editingPathway, setEditingPathway] = useState(false);
  const standing = STANDINGS.find((s) => s === standingParam) ?? null;
  const list = useMembershipList(standing);
  const needle = search.trim().toLowerCase();
  const rows = (list.data ?? []).filter(
    (r) => needle === "" || `${r.firstName} ${r.lastName}`.toLowerCase().includes(needle),
  );
  const tableStatus = list.isPending ? "loading" : list.isError ? "error" : "ready";
  return (
    <>
      <PageHeader
        title="Membership"
        description="Where each person stands, and how far along the pathway they are. A change is on their timeline."
        actions={
          <Button
            variant="light"
            leftSection={<IconRoute size={16} />}
            onClick={() => {
              setEditingPathway(true);
            }}
          >
            Pathway
          </Button>
        }
      />
      <FilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name"
        filters={
          <Select
            aria-label="Filter by standing"
            placeholder="Any standing"
            clearable
            data={STANDINGS.map((s) => ({ value: s, label: STANDING_LABEL[s] }))}
            value={standing}
            onChange={(value) => {
              setStandingParam(value ?? undefined);
            }}
            w={200}
          />
        }
      />
      <DataTable
        label="Membership"
        columns={columns}
        rows={rows}
        totalRows={rows.length}
        status={tableStatus}
        pagination={PAGE}
        onPaginationChange={() => undefined}
        sorting={[]}
        onSortingChange={() => undefined}
        getRowId={(row) => row.personId}
        onRowClick={(row) => {
          setSelected(row.personId);
        }}
        emptyTitle="Nobody yet"
        emptyDescription="Set a person's standing from their record; they appear here."
        errorMessage="Could not load membership."
        onRetry={() => {
          void list.refetch();
        }}
      />
      <PersonDrawer
        personId={selected === "" ? null : selected}
        onClose={() => {
          setSelected(undefined);
        }}
      />
      <PathwayModal
        opened={editingPathway}
        canManage={canManage.data === true}
        onClose={() => {
          setEditingPathway(false);
        }}
      />
    </>
  );
}

export type { Standing };
